import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ExternalLink } from 'lucide-react';

export interface NavItem {
  id: string;
  label: string;
  isExternal?: boolean;
  href?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About Randon' },
  { id: 'resume', label: 'Experience' },
  { id: 'services', label: 'Services' },
  { id: 'prices', label: 'Prices' },
  { id: 'estimator', label: 'Price Estimator' },
  { id: 'expertise', label: 'Expertise' },
  { id: 'portfolio', label: 'Project Showcase' },
  { id: 'websites', label: 'Websites' },
  { id: 'books', label: 'Books' },
  { id: 'apps', label: 'AI Apps' },
  { id: 'contact', label: 'Contact' },
  { id: 'terms', label: 'Terms & Conditions' },
];

interface NavigationHubProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTab: (tabId: string) => void;
  currentTab: string;
}

export const NavigationHub: React.FC<NavigationHubProps> = ({
  isOpen,
  onClose,
  onSelectTab,
  currentTab,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSelect = (id: string) => {
    onSelectTab(id);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="fixed inset-0 z-[100] bg-[#070709]/98 backdrop-blur-2xl overflow-y-auto custom-scrollbar select-none"
        >
          {/* Subtle Ambient Golden Glow Behind the Screen */}
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(245,158,11,0.08)_0%,transparent_70%)] pointer-events-none" />

          {/* Clean Close Button (Top Right) */}
          <button
            onClick={onClose}
            aria-label="Close navigation"
            className="fixed top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/[0.04] hover:bg-brand-500/15 border border-white/[0.08] hover:border-brand-500/40 text-slate-400 hover:text-brand-300 flex items-center justify-center transition-all duration-200 cursor-pointer group active:scale-95 z-50 shadow-lg"
          >
            <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
          </button>

          {/* Scrollable Centered Content Area with Safe Top/Bottom Padding to Prevent Cutoff */}
          <div className="min-h-full w-full flex flex-col items-center justify-center px-4 sm:px-8 py-14 sm:py-20">
            <div className="w-full max-w-5xl mx-auto flex flex-col items-center">
              
              {/* Title Header */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, duration: 0.3 }}
                className="text-center mb-6 sm:mb-10"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-extrabold text-white tracking-tight">
                  Where would you like to go next?
                </h2>
              </motion.div>

              {/* Three Column Grid Layout - 12 items forming 4 rows of 3 columns */}
              <div 
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-7 w-full"
                onMouseLeave={() => setHoveredId(null)}
              >
                {NAV_ITEMS.map((item, index) => {
                  const isCurrent = currentTab === item.id;
                  const isHovered = hoveredId === item.id;
                  const isGlowing = isCurrent || isHovered;

                  const cardClasses = `relative py-4 sm:py-5 md:py-6 px-4 sm:px-6 rounded-2xl flex items-center justify-center gap-2 text-center transition-all duration-300 cursor-pointer w-full ${
                    isGlowing
                      ? 'bg-brand-500/[0.08] border border-brand-500/40 shadow-[0_0_30px_rgba(245,158,11,0.22)] scale-[1.02]'
                      : 'bg-white/[0.02] border border-white/[0.06] hover:border-brand-500/30'
                  }`;

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.02 * index, duration: 0.2 }}
                      className="w-full"
                    >
                      {item.isExternal ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onClose}
                          onMouseEnter={() => setHoveredId(item.id)}
                          className={cardClasses}
                        >
                          {/* Golden Ambient Halo */}
                          <div
                            className={`absolute inset-0 rounded-2xl transition-opacity duration-300 pointer-events-none ${
                              isGlowing
                                ? 'opacity-100 bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.18),transparent_70%)]'
                                : 'opacity-0'
                            }`}
                          />

                          {/* Link Label with Golden Glow */}
                          <span
                            className={`relative z-10 text-xl sm:text-2xl md:text-3xl font-display font-black tracking-tight transition-all duration-300 flex items-center justify-center gap-2 ${
                              isGlowing
                                ? 'text-brand-400 drop-shadow-[0_0_18px_rgba(245,158,11,0.85)] scale-105'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            <span>{item.label}</span>
                            <ExternalLink className="w-4 h-4 text-brand-400 opacity-80 shrink-0" />
                          </span>
                        </a>
                      ) : (
                        <button
                          onClick={() => handleSelect(item.id)}
                          onMouseEnter={() => setHoveredId(item.id)}
                          className={cardClasses}
                        >
                          {/* Golden Ambient Halo */}
                          <div
                            className={`absolute inset-0 rounded-2xl transition-opacity duration-300 pointer-events-none ${
                              isGlowing
                                ? 'opacity-100 bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.18),transparent_70%)]'
                                : 'opacity-0'
                            }`}
                          />

                          {/* Link Label with Golden Glow */}
                          <span
                            className={`relative z-10 text-xl sm:text-2xl md:text-3xl font-display font-black tracking-tight transition-all duration-300 ${
                              isGlowing
                                ? 'text-brand-400 drop-shadow-[0_0_18px_rgba(245,158,11,0.85)] scale-105'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            {item.label}
                          </span>
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>

            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NavigationHub;
